import React, { useState, useContext } from 'react';
import { Form, Button, Row, Col, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axiosAPIinstance from '../config/axiosAPIinstance';
import { UserContext } from '../context/UserContext';
import BackButton from './BackButton';

function CreateAccount() {
  const navigate = useNavigate();
  const { login } = useContext(UserContext);

  const [step, setStep] = useState(1);
  const [accId, setAccId] = useState('');
  const [userName, setUserName] = useState('');
  const [accPwd, setAccPwd] = useState('');
  const [accConfirmPwd, setAccConfirmPwd] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [passwordMatched, setPasswordMatched] = useState(true);
  const [passwordLength, setPasswordLength] = useState(true);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showModal, setShowModal] = useState(false);

  const handleAccountId = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setAccId(value);
    }
  };

  const handleUserNameChange = (e) => setUserName(e.target.value);

  const handleOtpInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) setOtpCode(value);
  };

  const checkPassword = (pwd, confirmPwd) => {
    if (pwd.length >= 4 && pwd.length <= 6 && confirmPwd.length >= 4 && confirmPwd.length <= 6) {
      if (pwd === confirmPwd) {
        setPasswordMatched(true);
        setEnableSubmit(accId.length === 10);
      } else {
        setPasswordMatched(false);
        setEnableSubmit(false);
      }
    }
  };

  const handleAccPwd = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setAccPwd(value);
    setPasswordLength(false);
    if (value.length >= 4 && value.length <= 6) {
      checkPassword(value, accConfirmPwd);
      setPasswordLength(true);
    } else {
      setEnableSubmit(false);
    }
  };

  const handleAccConfirmPwd = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setAccConfirmPwd(value);
    setPasswordLength(false);
    if (value.length >= 4 && value.length <= 6) {
      checkPassword(accPwd, value);
      setPasswordLength(true);
    } else {
      setEnableSubmit(false);
    }
  };

  const handleGenerateOtp = async () => {
    setLoading(true);
    try {
      const response = await axiosAPIinstance.post("/user/generateNewUserOtp", {
        mobileNumber: accId,
        userName: userName,
      });

      if (response.data === "User Account Exists, Please user PIN Reset Option") {
        setModalMessage(response.data);
        setShowModal(true);
        setTimeout(() => {
          navigate("/forgot-pin", { state: { mobileNumber: accId } });
        }, 3000);
      } else {
        setModalMessage(response.data);
        setShowModal(true);
        setStep(2);
      }
    } catch (error) {
      setModalMessage("Failed to generate OTP. Please try again.");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosAPIinstance.post("/user/createUserAccount", {
        mobileNumber: accId,
        passwordorPin: accPwd,
        otpCode: otpCode,
      });

      if (response.data === "New User Created") {
        setModalMessage(response.data);
        login(response.data, accId);
      } else {
        setModalMessage("Invalid OTP. Please try again.");
        setOtpCode('');
        setAccPwd('');
        setAccConfirmPwd('');
        setEnableSubmit(false);
      }
    } catch (error) {
      setModalMessage("Account creation failed. Please try again.");
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6">
          <Form className="p-4 border rounded shadow-sm bg-light">
            <h3 className="mb-3 text-center">Create Account</h3>
  
            <Form.Group className="mb-3">
              <Form.Label>LoginID / Mobile Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Mobile Number"
                value={accId}
                onChange={handleAccountId}
              />
            </Form.Group>
  
            {step === 1 && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>User Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Full Name"
                    value={userName}
                    onChange={handleUserNameChange}
                  />
                </Form.Group>
  
                <Button variant="primary" disabled={accId.length !== 10} onClick={handleGenerateOtp}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Generate OTP'}
                </Button>
              </>
            )}
  
            {step === 2 && (
              <>
                <Form.Group className="mb-3 mt-3">
                  <Form.Label>Enter OTP</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter OTP"
                    value={otpCode}
                    onChange={handleOtpInputChange}
                  />
                </Form.Group>
  
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>PIN</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="4 to 6 digits"
                        value={accPwd}
                        onChange={handleAccPwd}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group>
                      <Form.Label>Confirm PIN</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm PIN"
                        value={accConfirmPwd}
                        onChange={handleAccConfirmPwd}
                      />
                    </Form.Group>
                  </Col>
                </Row>
  
                {!passwordLength && (
                  <div className="text-danger mt-2">Password length must be between 4 and 6 digits.</div>
                )}
                {!passwordMatched && (
                  <div className="text-danger mt-2">Passwords do not match.</div>
                )}
  
                <Button
                  className="mt-3"
                  variant="success"
                  onClick={handleCreateAccount}
                  disabled={!enableSubmit}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : 'Create Account'}
                </Button>
              </>
            )}
  
            <BackButton />
          </Form>
        </div>
      </div>
  
      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body className="text-center">
          <p>{modalMessage}</p>
          <Button variant="secondary" onClick={() => setShowModal(false)}>OK</Button>
        </Modal.Body>
      </Modal>
    </div>
  );
  
}

export default CreateAccount;
